import React from "react";
import dynamic from "next/dynamic";
import { useAppState } from "hooks/AppContext";
import { ArticleHeader } from "./ArticleHeader";
import { LeadMediaModule } from "./LeadMediaModule";
import { ArticleSocial } from "./ArticleSocial";
import { ArticleContent } from "./ArticleContent";
import { SponsorModule } from "./SponsorModule";
import { RecentArticlesProps } from "./RecentArticles";
import { NewsletterCardModuleProps } from "./Newsletter";
import { trackingData } from "./newsletterJobAlerts.constants";

import styles from "./articleContainer.module.scss";

const DynamicRecentArticles = dynamic<RecentArticlesProps>(
    () => import("./RecentArticles").then((x) => x.RecentArticles),
    { ssr: false }
);

const DynamicNewsletterCard = dynamic<NewsletterCardModuleProps>(
    () => import("./Newsletter").then((x) => x.NewsletterCard),
    { ssr: false }
);

const NewsletterJobAlerts = dynamic(
    () => import("./NewsletterJobAlerts").then((x) => x.NewsletterJobAlerts),
    { ssr: false }
);

export const ArticleContainer = () => {
    const { article } = useAppState();
    const {
        articleContentModules,
        articleUri,
        blockHeadingPrefix,
        breadcrumbLinks,
        contentAuthor,
        leadMedia,
        listBuilder,
        listicleContentType,
        postListBodyContent,
        product,
        slug,
        sponsor,
        tags,
        templateName,
        title,
        componentProps,
        dateUpdated,
        postDate,
        primaryTopic
    } = article;

    const social = {
        articleUri,
        slug,
        title,
        writerName: contentAuthor?.writerName,
        writerSlug: contentAuthor?.writerSlug,
        twitterHandle: contentAuthor?.twitterHandle,
        dateUpdated: dateUpdated ?? postDate
    };

    const author = {
        articleUri,
        slug,
        title,
        ...contentAuthor
    };

    const sponsorModuleProps = {
        listicleContentType,
        tags,
        product,
        ...sponsor,
        componentProps
    };

    const articleContentProps = {
        articleContentModules,
        postListBodyContent,
        blockHeadingPrefix,
        listBuilder,
        templateName,
        sponsorModuleProps: { ...sponsorModuleProps },
        author,
        dateUpdated,
        postDate
    };

    const [newsletterCardViewed, setNewsletterCardViewed] = React.useState(false);

    return (
        <article className={styles.article}>
            <div className={styles.articleContainer}>
                <ArticleHeader articleTitle={title} breadcrumbs={breadcrumbLinks} />
                <div className={styles.articleBannerFlexContainer}>
                    <div className={styles.articleBody}>
                        <ArticleSocial {...social} />
                        <div className={styles.articleBodyWrapper}>
                            <div className={styles.articleContentWrapper}>
                                {leadMedia && (
                                    <LeadMediaModule {...leadMedia} showPinterest={true} />
                                )}
                                <ArticleContent {...articleContentProps} />
                                <SponsorModule {...{ sponsorType: "midway", ...sponsorModuleProps }} />
                                <div className={styles.articlePostBodyWrapper}>
                                    <NewsletterJobAlerts {...{ className: styles.newsJobAlertsMobile, trackingData }} />
                                    <DynamicRecentArticles primaryTopic={primaryTopic} />
                                </div>
                            </div>
                            <div className={styles.rightRailWrapper}>
                                <div className={styles.rightRail}>
                                    <SponsorModule {...{ sponsorType: "sidebar", ...sponsorModuleProps }} />
                                    <NewsletterJobAlerts {...{ trackingData }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
};
